import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"

export type FeatureItem = {
  icon: string
  title: string
  description: string
}

export type FeaturesProps = SectionBase & {
  eyebrow: string
  title: string
  subtitle: string
  features: FeatureItem[]
  /** "grid" | "list" */
  layout: "grid" | "list"
  columns: 2 | 3 | 4
}

export const Features: ComponentConfig<FeaturesProps> = {
  fields: {
    layout: {
      type: "radio",
      options: [
        { value: "grid", label: "Grid" },
        { value: "list", label: "List" },
      ],
    },
    columns: {
      type: "radio",
      options: [
        { value: 2, label: "2 columns" },
        { value: 3, label: "3 columns" },
        { value: 4, label: "4 columns" },
      ],
    },
    eyebrow: { type: "text", label: "Eyebrow", contentEditable: true },
    title: { type: "textarea", contentEditable: true },
    subtitle: { type: "textarea", contentEditable: true },
    features: {
      type: "array",
      arrayFields: {
        icon: { type: "text", label: "Icon (emoji or SVG path)", contentEditable: true },
        title: { type: "text", contentEditable: true },
        description: { type: "textarea", contentEditable: true },
      },
      defaultItemProps: { icon: "✨", title: "New Feature", description: "Description of your feature goes here." },
      getItemSummary: (item) => item?.title || "Feature",
    },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  defaultProps: {
    layout: "grid",
    columns: 3,
    eyebrow: "",
    title: "Everything you need",
    subtitle: "Powerful features to help you build and grow.",
    features: [],
    background: "none",
    paddingY: "lg",
  },

  render: ({ layout, columns, eyebrow, title, subtitle, features, background, paddingY }) => {
    const s = sectionStyle({ background, paddingY })
    const gridStyle: React.CSSProperties = {
      display: "grid",
      gridTemplateColumns: `repeat(${layout === "list" ? 1 : columns}, 1fr)`,
      gap: layout === "list" ? 16 : 32,
      marginTop: 48,
    }

    return (
      <section style={s}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center" }}>
            {eyebrow && <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--site-accent, #7c3aed)", marginBottom: 12 }}>{eyebrow}</p>}
            <h2 style={{ fontSize: "clamp(1.75rem,4vw,2.5rem)", fontWeight: 800, marginBottom: 16, lineHeight: 1.2 }}>{title}</h2>
            {subtitle && <p style={{ fontSize: "1.125rem", color: "#6b7280", maxWidth: 640, margin: "0 auto" }}>{subtitle}</p>}
          </div>
          {features.length > 0 && <div style={gridStyle}>
            {features.map((f, i) => (
              <div key={i} style={{ padding: layout === "list" ? "16px 20px" : 0, background: layout === "list" ? "#fff" : "transparent", borderRadius: 12, border: layout === "list" ? "1px solid #e5e7eb" : "none" }}>
                {f.icon && <div style={{ fontSize: 32, marginBottom: 12 }}>{f.icon}</div>}
                <h3 style={{ fontSize: "1.125rem", fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ color: "#6b7280", lineHeight: 1.65 }}>{f.description}</p>
              </div>
            ))}
          </div>}
        </div>
      </section>
    )
  },
}